import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Superadmin diagnostic tool: traces an inbound email address end-to-end and
// explains why components parsed from it may not be visible in an
// organization's inbox. Backed by GET /admin/diagnostics/email-visibility.
const EmailVisibilityDiagnostics = () => {
  const [email, setEmail] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('email') || '';
    } catch {
      return '';
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [diagnosis, setDiagnosis] = useState(null);

  // Run once on mount when an ?email= query param prefills the input
  useEffect(() => {
    if (email) {
      runDiagnosis(email);
    }
  }, []); // eslint-disable-line

  const runDiagnosis = async (address) => {
    const target = (address || email).trim();
    if (!target || !target.includes('@')) {
      setError('Enter a full inbound email address (e.g. acme@in.veyage.io)');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setDiagnosis(null);
      const response = await axios.get(
        `/admin/diagnostics/email-visibility?email=${encodeURIComponent(target)}`
      );
      if (response.data?.success) {
        setDiagnosis(response.data.diagnosis);
      } else {
        setError('Diagnosis failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Diagnosis failed');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value);
    }
  };

  const OrgBadge = ({ org }) => {
    if (!org) return <span className="text-gray-400">—</span>;
    if (org.found === false) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          Org {org.id} not found
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1">
        <span className="font-medium text-gray-900">{org.name}</span>
        {org.is_active === false && (
          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">inactive</span>
        )}
        <span className="text-xs text-gray-400 font-mono">{org.id}</span>
      </span>
    );
  };

  const BoolPill = ({ value, trueLabel = 'yes', falseLabel = 'no', invert = false }) => {
    const good = invert ? !value : !!value;
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
        good ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {value ? trueLabel : falseLabel}
      </span>
    );
  };

  const SectionCard = ({ title, subtitle, children }) => (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">🩺 Email Visibility Diagnostics</h1>
        <p className="text-sm text-gray-600 mt-1">
          Trace an inbound email address end-to-end: which organization it routes to, where parsed
          components actually landed, and which visibility rules would hide them.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runDiagnosis()}
            placeholder="e.g. woven@in.veyage.io"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => runDiagnosis()}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Running…' : 'Run Diagnosis'}
          </button>
        </div>
        {error && (
          <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {diagnosis && (
        <>
          {/* Warnings */}
          {(diagnosis.warnings || []).length > 0 ? (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-2">⚠️ Warnings</h3>
              <ul className="list-disc list-inside space-y-1">
                {diagnosis.warnings.map((w, i) => (
                  <li key={i} className="text-sm text-amber-800">{w}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
              ✅ No routing warnings detected for this address.
            </div>
          )}

          {/* Resolution */}
          <SectionCard
            title="Current Routing"
            subtitle="Where an email sent to this address would land right now"
          >
            {diagnosis.resolved_record ? (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                <div>
                  <span className="text-gray-500 mr-1">Address record:</span>
                  <span className="font-mono text-xs">{diagnosis.resolved_record.email_address}</span>
                </div>
                <div>
                  <span className="text-gray-500 mr-1">Type:</span>
                  <span className="font-medium">{diagnosis.resolved_record.email_type}</span>
                </div>
                <div>
                  <span className="text-gray-500 mr-1">Organization:</span>
                  <OrgBadge org={diagnosis.resolved_record.organization} />
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                No active inbound address record resolves — see warnings above for what the webhook
                would do instead.
              </p>
            )}
            {(diagnosis.equivalent_addresses || []).length > 1 && (
              <p className="text-xs text-gray-400 mt-2">
                Equivalent addresses tried: {[...new Set(diagnosis.equivalent_addresses)].join(', ')}
              </p>
            )}
          </SectionCard>

          {/* Candidate organizations */}
          <SectionCard
            title={`Organizations Claiming This Address (${(diagnosis.candidate_organizations || []).length})`}
            subtitle="Orgs whose slug, name, or general inbox email matches — more than one usually means a duplicate/recreated org"
          >
            {(diagnosis.candidate_organizations || []).length === 0 ? (
              <p className="text-sm text-gray-500">None found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="pr-4 py-1">Name</th>
                      <th className="pr-4 py-1">Active</th>
                      <th className="pr-4 py-1">Slug</th>
                      <th className="pr-4 py-1">General Inbox</th>
                      <th className="pr-4 py-1">Created</th>
                      <th className="pr-4 py-1">ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {diagnosis.candidate_organizations.map((org) => (
                      <tr key={org.id} className={org.is_active ? '' : 'bg-red-50'}>
                        <td className="pr-4 py-2 font-medium text-gray-900">{org.name}</td>
                        <td className="pr-4 py-2"><BoolPill value={org.is_active} trueLabel="active" falseLabel="inactive" /></td>
                        <td className="pr-4 py-2 font-mono text-xs">{org.email_slug || '—'}</td>
                        <td className="pr-4 py-2 font-mono text-xs">{org.general_inbox_email || '—'}</td>
                        <td className="pr-4 py-2 text-xs text-gray-500">{formatDate(org.created_at)}</td>
                        <td className="pr-4 py-2 font-mono text-xs text-gray-400">{org.id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          {/* Address records */}
          <SectionCard
            title={`Inbound Address Records (${(diagnosis.address_records || []).length})`}
            subtitle="Every inbound_email_addresses record matching this address, including stale ones the webhook skips"
          >
            {(diagnosis.address_records || []).length === 0 ? (
              <p className="text-sm text-gray-500">No records found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="pr-4 py-1">Address</th>
                      <th className="pr-4 py-1">Type</th>
                      <th className="pr-4 py-1">Active</th>
                      <th className="pr-4 py-1">Passes Stale Check</th>
                      <th className="pr-4 py-1">Organization</th>
                      <th className="pr-4 py-1">Emails Received</th>
                      <th className="pr-4 py-1">Last Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {diagnosis.address_records.map((rec) => (
                      <tr key={rec.id} className={rec.passes_stale_check ? '' : 'bg-red-50'}>
                        <td className="pr-4 py-2 font-mono text-xs">{rec.email_address}</td>
                        <td className="pr-4 py-2">{rec.email_type}</td>
                        <td className="pr-4 py-2"><BoolPill value={rec.is_active} /></td>
                        <td className="pr-4 py-2"><BoolPill value={rec.passes_stale_check} trueLabel="valid" falseLabel="STALE" /></td>
                        <td className="pr-4 py-2"><OrgBadge org={rec.organization} /></td>
                        <td className="pr-4 py-2 text-center">{rec.total_emails_received ?? '—'}</td>
                        <td className="pr-4 py-2 text-xs text-gray-500">{formatDate(rec.last_email_received_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          {/* Parsing logs */}
          <SectionCard
            title={`Recent Parsing Logs (${(diagnosis.recent_parsing_logs || []).length})`}
            subtitle="Last 10 emails received at this address, across ALL organizations"
          >
            {(diagnosis.recent_parsing_logs || []).length === 0 ? (
              <p className="text-sm text-gray-500">No parsing logs found for this address.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="pr-4 py-1">Received</th>
                      <th className="pr-4 py-1">From</th>
                      <th className="pr-4 py-1">Subject</th>
                      <th className="pr-4 py-1">Status</th>
                      <th className="pr-4 py-1">Organization</th>
                      <th className="pr-4 py-1">Components</th>
                      <th className="pr-4 py-1">Sent to Review</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {diagnosis.recent_parsing_logs.map((log) => (
                      <tr key={log.id}>
                        <td className="pr-4 py-2 text-xs text-gray-500 whitespace-nowrap">{formatDate(log.received_at)}</td>
                        <td className="pr-4 py-2 font-mono text-xs">{log.from_email}</td>
                        <td className="pr-4 py-2 max-w-xs truncate" title={log.subject}>{log.subject || '—'}</td>
                        <td className="pr-4 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            log.processing_status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : log.processing_status === 'error' || log.processing_status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {log.processing_status}
                          </span>
                        </td>
                        <td className="pr-4 py-2"><OrgBadge org={log.organization} /></td>
                        <td className="pr-4 py-2 text-center">{(log.created_components || []).length}</td>
                        <td className="pr-4 py-2 text-center">{(log.routed_to_qa || []).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          {/* Components */}
          <SectionCard
            title={`Created Components (${(diagnosis.components || []).length})`}
            subtitle="Where each component actually lives, and what would hide it from the expected organization's inbox"
          >
            {(diagnosis.components || []).length === 0 ? (
              <p className="text-sm text-gray-500">
                No components were created from the recent parsing logs above.
              </p>
            ) : (
              <div className="space-y-3">
                {diagnosis.components.map((comp) => (
                  <div
                    key={comp.id}
                    className={`border rounded-lg p-3 ${
                      (comp.visibility_blockers || []).length > 0
                        ? 'border-red-300 bg-red-50'
                        : 'border-green-200 bg-green-50'
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      <span className="font-medium text-gray-900">
                        {comp.title || comp.component_type || comp.id}
                      </span>
                      {comp.component_type && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {comp.component_type}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">location: {comp.location}</span>
                      {comp.location !== 'NOT_FOUND' && (
                        <span className="text-xs"><OrgBadge org={comp.organization} /></span>
                      )}
                      {comp.created_at && (
                        <span className="text-xs text-gray-500">created {formatDate(comp.created_at)}</span>
                      )}
                    </div>
                    {comp.location === 'unfilled_components' && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-600">
                        <span>executive: {comp.executive_name || comp.executive_id || 'none'}</span>
                        <span>unassigned: {String(comp.is_unassigned)}</span>
                        <span>assigned to trip: {String(comp.assigned_to_trip)}</span>
                        <span>confidential: {String(comp.is_confidential_inbox)}</span>
                        {comp.created_by_user_name && <span>creator: {comp.created_by_user_name}</span>}
                      </div>
                    )}
                    {(comp.visibility_blockers || []).length > 0 ? (
                      <ul className="mt-2 space-y-1">
                        {comp.visibility_blockers.map((b, i) => (
                          <li key={i} className="text-sm text-red-700 flex gap-1.5">
                            <span>🚫</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-sm text-green-700">
                        ✅ Visible in this organization's inbox (no blockers detected)
                      </p>
                    )}
                    <p className="mt-1 text-xs font-mono text-gray-400">{comp.id}</p>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
};

export default EmailVisibilityDiagnostics;
